/* global process */
/**
 * DEV-ONLY tenant replication override.
 *
 * Lets a local dev session boot as any tenant by seeding `client` + `system_settings`
 * from a captured snapshot, so tenant-gated branches (isLegacyCalendar, newProgramInterface,
 * enableThirdPartyIntegrations effects, showKidsMode, ...) render without needing that
 * tenant's real account on the backend.
 *
 * Consumed at the two store chokepoints — `organization.getClient/getTenant` and
 * `systemSettings.getSystemSettings` — so every downstream hook (useOrganization,
 * useSystemSettings) resolves against the override transparently.
 *
 * Sources, first match wins:
 *   1) `__tenantOverride` in localStorage — pasted by hand in the DevTools console.
 *   2) `window.__TENANT_OVERRIDE__` — set by `/__tenant-override.js`, which only the rsbuild dev
 *      server serves (from `.tenant-override.local.json`, written by the `replicate-tenant` skill).
 *
 * SAFETY: returns null when NODE_ENV === 'production', whatever the sources hold. It can never
 * activate in a production build.
 *
 * The override is read once per page load (cached), so changing it requires a reload.
 *
 * Shape (any field optional; missing fields fall back to the real store):
 *   { "client": "<guid>", "tenantData": {...}, "flags": { "enable_library": 0, ... } }
 * `flags` are merged on top of the real system settings. The legacy `systemSettings` field
 * REPLACES them instead, so it must be the complete object.
 */
export const TENANT_OVERRIDE_KEY = '__tenantOverride';
export const TENANT_OVERRIDE_GLOBAL = '__TENANT_OVERRIDE__';

let cached;
let mergedFrom;
let merged;

function readOverride() {
  const raw = globalThis.localStorage?.getItem(TENANT_OVERRIDE_KEY);
  if (raw) return { ...JSON.parse(raw), source: 'localStorage' };
  const served = globalThis[TENANT_OVERRIDE_GLOBAL];
  return served ? { ...served, source: 'dev server' } : null;
}

export function getTenantOverride() {
  if (process.env.NODE_ENV === 'production') return null;
  if (cached !== undefined) return cached;

  try {
    cached = readOverride();
  } catch (error) {
    console.warn('[tenantOverride] ignoring malformed __tenantOverride value', error);
    cached = null;
  }

  if (cached) {
    console.info(`[tenantOverride] DEV tenant replication active (${cached.source}):`, cached.slug ?? cached.client ?? '(system_settings only)');
  }

  return cached;
}

/**
 * Real system settings → the ones the app should see. Without real settings it returns them
 * untouched: the settings saga only fetches when this selector comes back empty.
 * The merge is cached per settings identity because useSelector compares by reference.
 */
export function applySettingsOverride(settings) {
  const override = getTenantOverride();
  if (override?.systemSettings) return override.systemSettings;
  if (!override?.flags || !settings) return settings;

  if (mergedFrom !== settings) {
    mergedFrom = settings;
    merged = { ...settings, ...override.flags };
  }
  return merged;
}
